import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import {
  PartnerDealBriefingSectionResponse,
  PartnerDealInvestigationProgress,
  PartnerDealInvestigationResponse,
} from '@interfaces/partners/responses/partner-deal-briefing.response';
import { SharedModule } from '@src/app/shared/shared.module';
import { VaultDatePipe } from '@src/app/shared/pipes/vault-date.pipe';
import { VaultDateTimePipe } from '@src/app/shared/pipes/vault-date-time.pipe';
import { BriefingInvestigationProgressComponent } from '@src/app/features/dashboard/commercial/pages/deal-detail/components/deal-briefing-tab/briefing-investigation-progress/briefing-investigation-progress.component';
import { NO_RERUN_PERMISSION_HINT } from '@src/app/features/dashboard/commercial/pages/deal-detail/components/deal-briefing-tab/briefing-permissions';

/**
 * Estado da Investigação da empresa na aba Briefing (card 3911): rodando (com a fase e os números do tempo real), na
 * fila, pronta, reaproveitada de uma investigação anterior (com a validade) ou não concluída; e o "Refazer
 * investigação", que pede uma nova mesmo dentro da validade e exige `PARTNERDEALBRIEFINGINVESTIGATION_RERUN` (a mesma
 * que a API confere). Sem custo de IA.
 */
@Component({
  selector: 'app-briefing-investigation-status',
  standalone: true,
  imports: [SharedModule, VaultDatePipe, VaultDateTimePipe, BriefingInvestigationProgressComponent],
  templateUrl: './briefing-investigation-status.component.html',
  styleUrl: './briefing-investigation-status.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BriefingInvestigationStatusComponent {
  /** A investigação mais recente do CNPJ; nula quando nunca houve. */
  readonly investigation = input<PartnerDealInvestigationResponse | null>(null);
  /** Progresso ao vivo da investigação que roda (evento do tempo real). */
  readonly progress = input<PartnerDealInvestigationProgress | null>(null);
  /** Catálogo do briefing, para o nome de negócio da subseção que o agente aprofunda (card 3918). */
  readonly catalog = input<readonly PartnerDealBriefingSectionResponse[]>([]);
  /** A seção Investigação está marcada no roteiro. */
  readonly selectedInRoadmap = input(false);
  /** Pedido de refazer em andamento. */
  readonly rerunning = input(false);
  /** Prontidão bloqueada ou geração em andamento. */
  readonly disabled = input(false);
  /** `PARTNERDEALBRIEFINGINVESTIGATION_RERUN`: sem ela, Refazer fica desabilitado com a dica do motivo. */
  readonly canRerun = input(true);

  protected readonly noRerunHint = NO_RERUN_PERMISSION_HINT;

  readonly rerun = output<void>();

  protected readonly status = computed(() => this.investigation()?.status ?? null);
  protected readonly isRunning = computed(
    () => this.status() === 'running' || this.status() === 'queued'
  );
  /**
   * Linha de cima enquanto roda (card 3918): a fase e os números saíram para a lista de etapas
   * (`app-briefing-investigation-progress`); aqui fica só o aviso, com "Na fila" quando ainda não começou.
   */
  protected readonly runningText = computed(() =>
    this.status() === 'queued'
      ? 'Na fila. Leva alguns minutos; o briefing não espera.'
      : 'Leva alguns minutos; o briefing não espera.'
  );
  /** Recomendação da investigação pronta ("Cautela"), quando o agente concluiu. */
  protected readonly recommendation = computed(
    () => this.investigation()?.conclusion?.recommendationLabel ?? null
  );

  protected onRerun(): void {
    if (!this.canRerun() || this.disabled() || this.rerunning() || this.isRunning()) return;
    this.rerun.emit();
  }
}
